import { createSelector } from '@reduxjs/toolkit';

import { type GroupedAdditionalFieldConfigType, type IAdditionalFieldConfig } from '../types/response';
import { entityToFieldPrefixMap, FieldPrefix } from '../constants/fieldPrefixes';
import { supportInfoMessage } from '../types/fieldEnums';
import { DataType, Status, Tab } from '../types/enums';
import { ConfigOption } from '../types/configs';

import { getAdditionalFieldsConfigMap } from '../helpers/getFormValues';
import { getIsKeyOperation } from '../helpers/getIsKeyOperation';
import groupByBillNumber from '../helpers/groupByBillNumber';
import type { RootState } from '../../../store';
import { getIdMap } from '../../../helpers';

export const lpcoSelector = (state: RootState) => state.license.lpco;
export const parentLpcoSelector = (state: RootState) => state.license.parentLpco;

export const isChildLpcoSelector = (state: RootState) =>
  !!state.license.parentLpco.data || !!state.license.lpco.data?.parentLpcoId;

export const lpcoSubmittingSelector = (state: RootState) => state.license.lpco.isSubmitting;

export const lpcoLoadingSelector = (state: RootState) => state.license.lpco.loading;

export const itemsSelector = (state: RootState) => state.license.lpco.data?.items;
export const parentLpcoItemsSelector = (state: RootState) => state.license.parentLpco.data?.items;

export const attachmentsSelector = (state: RootState) => state.license.lpco.data?.attachedDocuments;
export const parentLpcoAttachmentsSelector = (state: RootState) => state.license.parentLpco.data?.attachedDocuments;

export const attachmentFileUrlSelector = createSelector(
  attachmentsSelector,
  (_: RootState, id?: number) => id,
  (attachments, id) => id && attachments?.find((attachment) => attachment.id === id)?.fileUrl
);

export const requestNumberSelector = (state: RootState) => state.license.lpco.data?.requestNumber;
export const approvalReferenceSelector = (state: RootState) => state.license.lpco.data?.approvalReference;

export const licenseStatusSelector = (state: RootState) => state.license.lpco.data?.status;

export const selectedLicenseTypeIdSelector = (state: RootState) => state.license.licenseType.selectedLicenseTypeId;

export const editableFieldsByOperationSelector = (operationType: string) => (state: RootState) => {
  const fields = state.license.configuration.configs?.operations[operationType];
  const mandatoryFields = fields?.mandatoryFields ?? [];
  const optionalFields = fields?.optionalFields ?? [];

  return mandatoryFields.concat(optionalFields);
};

export const supportInfoMessageConfigByOperationSelector = (operationType: string) => (state: RootState) => {
  const fields = state.license.configuration.configs?.operations[operationType];

  const fieldName = `${FieldPrefix.lpco}${supportInfoMessage}`;

  if (fields?.mandatoryFields.includes(fieldName)) {
    return ConfigOption.MANDATORY;
  }

  if (fields?.optionalFields.includes(fieldName)) {
    return ConfigOption.OPTIONAL;
  }

  return ConfigOption.PROHIBITED;
};

export const selectedLicenseTypeSelector = (state: RootState) => {
  const selectedLicenseTypeId = state.license.licenseType.selectedLicenseTypeId;

  return {
    data: selectedLicenseTypeId ? state.license.licenseType.idMap[selectedLicenseTypeId] : null,
    loading: state.license.licenseType.loading,
  };
};

export const selectedLicenseTypeCodeSelector = createSelector(
  selectedLicenseTypeSelector,
  (selectedLicenseType) => selectedLicenseType.data?.licenseTypeResponse.licenseTypeCode
);

export const parentLicenseTypeSelector = (state: RootState) => {
  const parentLicenseTypeId = state.license.licenseType.parentLicenseTypeId;

  return parentLicenseTypeId ? state.license.licenseType.idMap[parentLicenseTypeId] : null;
};

export const selectedLicenseTypeNatureSelector = createSelector(
  selectedLicenseTypeSelector,
  (selectedLicenseType) => selectedLicenseType?.data?.licenseTypeResponse?.licenseTypeNature
);

export const subLicenseTypesSelector = createSelector(
  selectedLicenseTypeSelector,
  (selectedLicenseType) => selectedLicenseType?.data?.licenseTypeResponse?.subLicenseTypes
);

export const selectedLicenseTypeTransitionsSelector = createSelector(
  selectedLicenseTypeSelector,
  (selectedLicenseType) => {
    if (selectedLicenseType.data) {
      const transitions = selectedLicenseType.data.licenseTypeResponse.approvals.flatMap((approval) =>
        approval.transitions.map((transition) => ({
          ...transition,
          id: `${transition.operationName}_${transition.id}`,
        }))
      );

      return getIdMap(transitions);
    }

    return {};
  }
);

export const maxNumOfDaysForExtensionSelector = createSelector(
  selectedLicenseTypeSelector,
  (selectedLicenseType) => selectedLicenseType.data?.licenseTypeResponse.maxNumOfDaysForExtension || 0
);

export const relatedProductsSelector = createSelector(
  selectedLicenseTypeSelector,
  (selectedLicenseType) => selectedLicenseType.data?.licenseTypeResponse.relatedProducts || ''
);

export const enabledQuotasSelector = createSelector(
  selectedLicenseTypeSelector,
  (selectedLicenseType) => selectedLicenseType.data?.licenseTypeResponse.enabledQuotas || []
);

export const defaultQuotaTypeSelector = createSelector(
  selectedLicenseTypeSelector,
  (selectedLicenseType) => selectedLicenseType.data?.licenseTypeResponse.quotaType
);

export const defaultAttachmentsSelector = createSelector(
  selectedLicenseTypeSelector,
  (selectedLicenseType) => selectedLicenseType.data?.licenseTypeResponse.attachedDocuments
);

export const featureFlagsSelector = createSelector(
  selectedLicenseTypeSelector,
  ({ data }) => data?.licenseTypeResponse.featureFlags
);

export const fieldConfigsSelector = createSelector(selectedLicenseTypeSelector, ({ data }) => {
  const fieldConfigs = data?.licenseTypeResponse.fieldConfigs || [];

  return fieldConfigs.reduce((acc, { fieldName, entityName, hidden }) => {
    const prefix = entityToFieldPrefixMap[entityName] ?? '';
    const key = `${prefix}${fieldName}`;
    acc[key] = { hidden };

    return acc;
  }, {} as Record<string, { hidden: boolean }>);
});

export const operationMessagesSelector = createSelector(
  selectedLicenseTypeSelector,
  (selectedLicenseType) => selectedLicenseType.data?.licenseTypeResponse?.operationMessages ?? []
);

export const downloadPermissionSelector = createSelector(
  selectedLicenseTypeSelector,
  lpcoSelector,
  (selectedLicenseType, lpco) => {
    const printStatuses = selectedLicenseType.data?.licenseTypeResponse.printStatuses;
    const lpcoStatus = lpco.data?.status;

    if (!printStatuses || !lpcoStatus) {
      return false;
    }

    if (lpcoStatus in Status) {
      return printStatuses.includes(lpcoStatus as Status);
    }

    return printStatuses.includes(Status.PARTIALLY_APPROVED);
  }
);

export const unionOfEditableFieldsSelector = (state: RootState) => state.license.configuration.unionOfEditableFields;

export const licenseTypeByIdSelector = (id: number) => (state: RootState) => state.license.licenseType.idMap[id];

export const beneficiariesSelector = (state: RootState) => state.license.lpco.data?.beneficiaries ?? [];

export const userReferenceNumberSelector = (state: RootState) => state.license.userReferenceNumber;

export const documentStatusesSelector = (state: RootState) => {
  if (state.license.lpco.data) {
    const { status, ogaStatus, ogaStatusInNationalLang } = state.license.lpco.data;

    return {
      status,
      ogaStatus,
      ogaStatusInNationalLang,
    };
  }

  return {
    status: '',
    ogaStatus: null,
    ogaStatusInNationalLang: null,
  };
};

export const operationsSelector = createSelector(
  (state: RootState) => state.license.configuration.configs?.operations,
  (operations) =>
    Object.keys(operations || {}).sort((a, b) => {
      if (getIsKeyOperation(a) && !getIsKeyOperation(b)) {
        return 1;
      }

      return -1;
    })
);

export const attachmentPermissionsSelector = (state: RootState) =>
  state.license.configuration.configs?.subDocs.attachedDocuments;

export const itemPermissionsSelector = (state: RootState) => state.license.configuration.configs?.subDocs.items;

export const feePermissionsSelector = (state: RootState) => state.license.configuration.configs?.subDocs.fees;

export const packagesPermissionsSelector = (state: RootState) => state.license.configuration.configs?.subDocs.packages;

export const treatmentPermissionsSelector = (state: RootState) =>
  state.license.configuration.configs?.subDocs.treatments;

export const feesSelector = createSelector(lpcoSelector, feePermissionsSelector, (lpco, permissions) => {
  if (!lpco.data) {
    return {
      fees: [],
      billNumber: '',
      amountToBePaid: 0,
      totalAmount: 0,
      modify: false,
    };
  }

  const { fees, billNumber, amountToBePaid, totalAmount } = lpco.data;

  return {
    fees: groupByBillNumber(fees),
    billNumber,
    amountToBePaid,
    totalAmount,
    modify: !!permissions?.modify,
  };
});

const defaultAdditionalFields: IAdditionalFieldConfig[] = [];

export const licenseTypeAdditionalFieldsSelector = createSelector(
  selectedLicenseTypeSelector,
  (selectedLicenseType) => {
    const additionalFields = selectedLicenseType.data?.licenseTypeResponse?.additionalFields;

    return additionalFields?.length ? additionalFields : defaultAdditionalFields;
  }
);

export const parentLicenseAdditionalFieldsSelector = createSelector(parentLicenseTypeSelector, (parentLicenseType) => {
  const additionalFields = parentLicenseType?.licenseTypeResponse?.additionalFields;

  return additionalFields?.length ? additionalFields : defaultAdditionalFields;
});

export const licenseTypeAdditionalFieldsByTabsSelector = createSelector(
  licenseTypeAdditionalFieldsSelector,
  (additionalFields) =>
    additionalFields.reduce((acc, field) => {
      const { tab, rowNumber, positionInRow } = field;

      if (!acc[tab]) {
        acc[tab] = [];
      }

      if (!acc[tab]![rowNumber]) {
        acc[tab]![rowNumber] = [];
      }

      if (!acc[tab]![rowNumber]![positionInRow]) {
        acc[tab]![rowNumber]![positionInRow] = field;
      }

      return acc;
    }, {} as GroupedAdditionalFieldConfigType)
);

export const additionalFieldsMapByTabSelector = createSelector(
  licenseTypeAdditionalFieldsSelector,
  (additionalFields) => {
    const additionalFieldsMap = {
      [Tab.HEADER]: {},
      [Tab.NAMES_AND_PARTIES]: {},
      [Tab.CONSIGNMENT]: {},
      [Tab.ITEM]: {},
      [Tab.ATTACHED_DOCUMENT]: {},
    } as Record<Tab, Record<string, string>>;

    additionalFields.forEach(({ tab, id, englishLabel }) => {
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      const fieldName = FieldPrefix.additionalField + id;

      if (additionalFieldsMap[tab]) {
        additionalFieldsMap[tab][fieldName] = englishLabel;
      }
    });

    return additionalFieldsMap;
  }
);

export const additionalFieldsConfigsMapSelector = createSelector(
  licenseTypeAdditionalFieldsSelector,
  getAdditionalFieldsConfigMap
);

export const additionalFieldIdsByNameSelector = createSelector(
  licenseTypeAdditionalFieldsSelector,
  (additionalFields) =>
    additionalFields.reduce(
      (acc, { tab, id, englishLabel, dataType }) => {
        const fieldPrefix =
          dataType === DataType.OPTIONS_FROM_DATABASE
            ? FieldPrefix.dbOptionAdditionalFields
            : FieldPrefix.additionalFields;

        if (tab === Tab.HEADER || tab === Tab.NAMES_AND_PARTIES || tab === Tab.CONSIGNMENT) {
          acc.lpcoFields[englishLabel] = `${fieldPrefix}${FieldPrefix.additionalField}${id}`;
        }

        if (tab === Tab.ITEM) {
          acc.itemFields[englishLabel] = `${fieldPrefix}${FieldPrefix.additionalField}${id}`;
        }

        return acc;
      },
      { lpcoFields: {} as Record<string, string>, itemFields: {} as Record<string, string> }
    )
);

export const errorsSelector = (state: RootState) => state.license.errors;
export const mandatoryAttachmentCodesFromErrorSelector = (state: RootState) => state.license.mandatoryAttachmentCodes;

export const hasBackendErrorSelector = (state: RootState) => !!state.license.errors.length;

export const validationConfigSelector = (state: RootState) => state.license.validationConfig;

export const queryMessageSelector = (state: RootState) => state.license.queryMessage;
